import { ApiError, type ProductImageRow, queryKeys } from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { BulkActionBar } from '../components/data-table.js'
import {
  ImageLibraryGrid,
  useDebouncedValue,
  useImageLibrary,
} from '../components/image-library-grid.js'
import { LibraryDocuments } from '../components/library-documents.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const deletable = (image: ProductImageRow) => image.usedBy.length === 0

/** Native checkboxes have no `indeterminate` prop — only a DOM property. */
function SelectAllCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean
  indeterminate: boolean
  onChange: () => void
}) {
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate
  }, [indeterminate])
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      id="select-all-images"
      className="size-4 accent-[var(--primary)]"
    />
  )
}

/** Runs `fn` over `ids`, `size` at a time, never throwing. */
async function inChunks(
  ids: string[],
  size: number,
  fn: (id: string) => Promise<unknown>,
): Promise<{ deleted: string[]; failed: { id: string; reason: string }[] }> {
  const deleted: string[] = []
  const failed: { id: string; reason: string }[] = []
  for (let i = 0; i < ids.length; i += size) {
    const chunk = ids.slice(i, i + size)
    const results = await Promise.allSettled(chunk.map(fn))
    results.forEach((r, j) => {
      const id = chunk[j] as string
      if (r.status === 'fulfilled') deleted.push(id)
      else
        failed.push({
          id,
          reason: r.reason instanceof ApiError ? r.reason.message : 'Something went wrong',
        })
    })
  }
  return { deleted, failed }
}

/**
 * Every picture uploaded to a product (or generated by the AI button) lives
 * in `product_images`; this is the browsable view over that table. Nothing is
 * copied in — an upload is in the library the moment it is saved.
 */
export function ImageLibraryPage() {
  const { can } = useAuth()
  const canWrite = can('catalog:write')
  const toast = useToast()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const q = useDebouncedValue(search.trim(), 250)
  const [deleting, setDeleting] = useState<ProductImageRow | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmBulk, setConfirmBulk] = useState(false)
  const [tab, setTab] = useState('images')
  const { items } = useImageLibrary(q)
  const selectableLoaded = items.filter(deletable)
  const selectedCount = selectableLoaded.filter((i) => selected.has(i.id)).length
  const allSelected = selectableLoaded.length > 0 && selectedCount === selectableLoaded.length

  // A new search shows different images — never delete ones that scrolled away.
  // biome-ignore lint/correctness/useExhaustiveDependencies: clear only when the search changes
  useEffect(() => {
    setSelected(new Set())
  }, [q])

  const toggle = (image: ProductImageRow) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(image.id)) next.delete(image.id)
      else next.add(image.id)
      return next
    })

  const bulkRemove = useMutation({
    mutationFn: () => {
      const ids = selectableLoaded.filter((i) => selected.has(i.id)).map((i) => i.id)
      return inChunks(ids, 4, (id) => api.delete<{ deleted: true }>(`/products/images/${id}`))
    },
    onSuccess: async ({ deleted, failed }) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.imageLibrary.all() })
      if (deleted.length > 0) {
        toast.success(`Deleted ${deleted.length} image${deleted.length === 1 ? '' : 's'}`)
      }
      if (failed.length > 0) {
        toast.error(
          `${failed.length} image${failed.length === 1 ? '' : 's'} couldn't be deleted`,
          failed[0]?.reason,
        )
      }
      setSelected(new Set())
      setConfirmBulk(false)
    },
    onError: () => {
      toast.error('Could not delete the images')
      void queryClient.invalidateQueries({ queryKey: queryKeys.imageLibrary.all() })
      setConfirmBulk(false)
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<{ deleted: true }>(`/products/images/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.imageLibrary.all() })
      toast.success('Image deleted')
      setDeleting(null)
    },
    onError: (err) => {
      toast.error('Could not delete the image', err instanceof ApiError ? err.message : undefined)
      void queryClient.invalidateQueries({ queryKey: queryKeys.imageLibrary.all() })
      setDeleting(null)
    },
  })

  return (
    <Page>
      <PageHeader
        title="Image library"
        description="Every picture uploaded to a product is saved here automatically. Pick one from here when editing any product or variation. PDF and Word files live on the Documents tab."
      />

      <Tabs
        value={tab}
        onValueChange={(next) => {
          setTab(next)
          setSelected(new Set())
        }}
      >
        <TabsList>
          <TabsTrigger value="images">Images</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>
        <TabsContent value="images">
          <div className="mb-3 flex flex-wrap items-center gap-4">
            <div className="w-full max-w-sm">
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by file name"
                aria-label="Search the image library"
              />
            </div>
            {canWrite && selectableLoaded.length > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <SelectAllCheckbox
                  checked={allSelected}
                  indeterminate={selectedCount > 0 && !allSelected}
                  onChange={() =>
                    setSelected(
                      allSelected ? new Set() : new Set(selectableLoaded.map((i) => i.id)),
                    )
                  }
                />
                <label htmlFor="select-all-images">Select all</label>
                {selectedCount > 0 && (
                  <span className="text-foreground-muted tabular">· {selectedCount} selected</span>
                )}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-border bg-surface">
            <ImageLibraryGrid
              q={q}
              selectedIds={canWrite ? selected : undefined}
              onToggleSelect={canWrite ? toggle : undefined}
              isSelectable={canWrite ? deletable : undefined}
              renderActions={
                canWrite
                  ? (image) => (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Delete ${image.filename ?? 'image'}`}
                        disabled={image.usedBy.length > 0}
                        title={
                          image.usedBy.length > 0
                            ? 'In use — change the image on those products first'
                            : 'Delete'
                        }
                        onClick={() => setDeleting(image)}
                      >
                        <Trash2 />
                      </Button>
                    )
                  : undefined
              }
            />
          </div>
        </TabsContent>
        <TabsContent value="documents">
          <LibraryDocuments canWrite={canWrite} />
        </TabsContent>
      </Tabs>

      <BulkActionBar count={selectedCount} onClear={() => setSelected(new Set())}>
        <Button variant="danger" size="sm" onClick={() => setConfirmBulk(true)}>
          <Trash2 /> Delete selected
        </Button>
      </BulkActionBar>

      <Dialog open={confirmBulk} onOpenChange={(open) => !open && setConfirmBulk(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Delete {selectedCount} image{selectedCount === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              No product uses {selectedCount === 1 ? 'this image' : 'these images'}. Deleting{' '}
              {selectedCount === 1 ? 'it removes it' : 'them removes them'} from the library for
              good.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmBulk(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={bulkRemove.isPending}
              onClick={() => bulkRemove.mutate()}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {deleting?.filename ?? 'this image'}?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm text-foreground-muted">
              No product uses this image. Deleting it removes it from the library for good.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting.id)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  )
}
